import {
  ensureSystemWorkspaceDoc,
  getSystemWorkspaceStore,
} from '@blocksuite/affine-block-streamlain';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import { type BlockModel, StoreSelectionExtension } from '@blocksuite/store';
import { readDatabaseShardDirectoryHead } from '@streamlain/database-identity';

export { ensureSystemWorkspaceDoc, getSystemWorkspaceStore };

/** Root document of a database block; every database block names it. */
export function requireDatabaseRootDocId(
  hostModel: DatabaseBlockModel
): string {
  const rootDocId = hostModel.props.databaseRootDocId;
  if (!rootDocId) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Database block "${hostModel.id}" is missing databaseRootDocId.`
    );
  }
  return rootDocId;
}

/** Database id of a database block; every database block names it. */
export function requireDatabaseId(model: DatabaseBlockModel): string {
  const databaseId = model.props.databaseId;
  if (!databaseId) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Database block "${model.id}" is missing databaseId.`
    );
  }
  return databaseId;
}

export function resolveDatabaseStorageModel(
  hostModel: DatabaseBlockModel
): DatabaseBlockModel {
  const rootDocId = requireDatabaseRootDocId(hostModel);
  if (rootDocId === hostModel.store.id) {
    assertDatabaseStorageManifest(hostModel, rootDocId);
    return hostModel;
  }

  const databaseId = requireDatabaseId(hostModel);
  const storageBlock = getDatabaseStorageBlock(
    hostModel,
    rootDocId,
    databaseId
  );
  if (!storageBlock || storageBlock.flavour !== 'affine:database') {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Database root document "${rootDocId}" does not contain database "${databaseId}".`
    );
  }

  assertDatabaseStorageManifest(storageBlock as DatabaseBlockModel, rootDocId);
  return storageBlock as DatabaseBlockModel;
}

export function assertDatabaseStorageManifest(
  storageModel: DatabaseBlockModel,
  rootDocId: string
): void {
  if (readDatabaseShardDirectoryHead(storageModel)) {
    return;
  }
  const databaseId = requireDatabaseId(storageModel);
  throw new BlockSuiteError(
    ErrorCode.DatabaseBlockError,
    `Database root document "${rootDocId}" has no row-shard manifest for database "${databaseId}".`
  );
}

/** Whether the head is stored; a malformed head throws, it never waits. */
export function hasValidDatabaseStorageManifest(
  storageModel: DatabaseBlockModel
): boolean {
  return readDatabaseShardDirectoryHead(storageModel) !== undefined;
}

export function getDatabaseStorageBlock(
  hostModel: DatabaseBlockModel,
  rootDocId: string,
  databaseId: string
): BlockModel | undefined {
  const rootDoc = getSystemWorkspaceStore(hostModel.store, rootDocId);
  return rootDoc?.getBlock(databaseId)?.model;
}

export function isDatabaseStorageModelReady(
  hostModel: DatabaseBlockModel
): boolean {
  const rootDocId = requireDatabaseRootDocId(hostModel);
  if (rootDocId === hostModel.store.id) {
    return hasValidDatabaseStorageManifest(hostModel);
  }

  const databaseId = requireDatabaseId(hostModel);
  const storageBlock = getDatabaseStorageBlock(
    hostModel,
    rootDocId,
    databaseId
  );
  return (
    storageBlock?.flavour === 'affine:database' &&
    hasValidDatabaseStorageManifest(storageBlock as DatabaseBlockModel)
  );
}

export function observeDatabaseStorageModelReady(
  hostModel: DatabaseBlockModel,
  onReady: () => void
): () => void {
  const rootDocId = requireDatabaseRootDocId(hostModel);
  if (rootDocId === hostModel.store.id) {
    return () => {};
  }

  const rootDocRecord = ensureSystemWorkspaceDoc(hostModel.store, rootDocId);
  const checkReady = () => {
    if (isDatabaseStorageModelReady(hostModel)) {
      onReady();
    }
  };
  rootDocRecord.spaceDoc.on('update', checkReady);
  checkReady();
  return () => rootDocRecord.spaceDoc.off('update', checkReady);
}

/**
 * A crewmate's change of the database root (its views) arrives in the root
 * document, outside the page parts: its pointer follows it there.
 */
export function observeDatabaseRootAgent(
  hostModel: DatabaseBlockModel
): () => void {
  const rootDocId = requireDatabaseRootDocId(hostModel);
  if (rootDocId === hostModel.store.id) return () => {};
  const rootDocRecord = ensureSystemWorkspaceDoc(hostModel.store, rootDocId);
  return hostModel.store.provider
    .get(StoreSelectionExtension)
    .observeAgentDoc(rootDocRecord.spaceDoc);
}
